import { AdminRole } from "@repo/api-dtos"
import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router"
import { Plus, X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { QuickFilter } from "@/components/promotions/grid-view-actions"
import {
	PROMOTION_STATUS_LABELS,
	PROMOTION_STATUSES,
} from "@/components/promotions/promotion-helpers"
import { ToolbarSearch } from "@/components/promotions/toolbar-search"
import { Button } from "@/components/ui/button"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { BuyerPromotionsAdminGrid } from "@/features/buyer-promotions/BuyerPromotionsAdminGrid"
import type { BuyerPromotionsAdminInput } from "@/features/buyer-promotions/buyer-promotions-datasource"
import { useAdminRoles } from "@/hooks/use-admin-roles"

const MAX_SEARCH_LENGTH = 100

/** URL search params of the buyer promotions list. */
export interface BuyerPromotionsSearch {
	page?: number
	search?: string
	/** Column-managed filter (CSV-encoded values, owned by the grid). */
	status?: string
}

export const Route = createFileRoute("/buyer-promotions/")({
	component: BuyerPromotionsPage,
	validateSearch: (search: Record<string, unknown>): BuyerPromotionsSearch => ({
		page: normalizePage(search.page),
		search: normalizeSearch(search.search),
		status: search.status as string | undefined,
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) {
		return undefined
	}

	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

function normalizeSearch(raw: unknown): string | undefined {
	if (typeof raw !== "string") {
		return undefined
	}

	return raw.trim().slice(0, MAX_SEARCH_LENGTH) || undefined
}

function BuyerPromotionsPage() {
	const searchParams = useSearch({ from: "/buyer-promotions/" })
	const navigate = useNavigate()
	const { hasRole } = useAdminRoles()
	const canOperate = hasRole(AdminRole.MarketplaceCoupons)

	const pageIndex = (searchParams.page ?? 1) - 1
	const searchRef = React.useRef<{ clear: () => void }>(null)

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/buyer-promotions",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) => {
			updateFilters({ search: normalizeSearch(value), page: undefined })
		},
		[updateFilters],
	)

	const gridInput = React.useMemo<BuyerPromotionsAdminInput>(
		() => (searchParams.search ? { search: searchParams.search } : {}),
		[searchParams.search],
	)

	const handlePageChange = React.useCallback(
		(nextPageIndex: number) => {
			updateFilters({
				page: nextPageIndex > 0 ? nextPageIndex + 1 : undefined,
			})
		},
		[updateFilters],
	)

	const clearFilters = React.useCallback(() => {
		searchRef.current?.clear()
		navigate({ to: "/buyer-promotions", search: {} as never, replace: true })
	}, [navigate])

	const hasActiveFilters = !!searchParams.status || !!searchParams.search

	const toggleStatus = React.useCallback(
		(value: string) => {
			updateFilters({
				status: searchParams.status === value ? undefined : value,
				page: undefined,
			})
		},
		[searchParams.status, updateFilters],
	)

	const toolbar = (
		<>
			<ToolbarSearch>
				<DebouncedSearchInput
					defaultValue={searchParams.search ?? ""}
					onDebouncedChange={handleDebouncedSearch}
					placeholder="Search name or key"
					inputRef={searchRef}
					maxLength={MAX_SEARCH_LENGTH}
				/>
			</ToolbarSearch>

			<div className="flex items-center gap-1">
				{PROMOTION_STATUSES.map((status) => (
					<QuickFilter
						key={status}
						active={searchParams.status === status}
						onToggle={() => toggleStatus(status)}
					>
						{PROMOTION_STATUS_LABELS[status]}
					</QuickFilter>
				))}
			</div>

			{hasActiveFilters && (
				<Button variant="ghost" onClick={clearFilters}>
					<X />
					Clear
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Buyer promotions"
				description="No buyer fee or an automatic discount at checkout, by delivery country and optionally by shop"
				actions={
					canOperate && (
						<Button asChild>
							<Link to="/buyer-promotions/new">
								<Plus />
								Create promotion
							</Link>
						</Button>
					)
				}
			/>

			<div className="min-h-0 flex-1">
				<BuyerPromotionsAdminGrid
					input={gridInput}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					toolbar={toolbar}
				/>
			</div>
		</div>
	)
}
